import React, { Fragment, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Divider,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  AppBar,
  Toolbar, FormControl, Select, MenuItem, Tabs, Tab, TextField, Radio, RadioGroup, FormControlLabel,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { HttpRequest } from "../../../utils/httpRequests";
import NavBar from "../../../components/NavBar/NavBar";
import CustomizedSnackbar from "../../../components/CustomizedSnackbar/CustomizedSnackbar";
import LoginMenu from "../../../components/LoginMenu/LoginMenu";
import { Skeleton } from "@material-ui/lab";
import PublishIcon from "@material-ui/icons/Publish";
import { Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import { InputAdornment } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  root: {
    padding: 10,
    margin: "70px 20px",
  },
  categoryHeading: {
    fontWeight: 700,
    marginBottom: 20,
    marginTop: 20,
  },
  productCard: {
    marginBottom: 20,
    padding: 15,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
      transform: "translateY(-2px)",
    },
  },
  productLabel: {
    fontWeight: 600,
    marginBottom: 15,
    color: "#333",
  },
  assetCard: {
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  assetImage: {
    // height: 200,
    // objectFit: "cover",
    // width: "100%",
    // height handled by aspect ratio wrapper in LazyImage
  },
  assetFileName: {
    fontSize: 12,
    color: "#666",
    wordBreak: "break-word",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "60vh",
  },
  loadMoreButton: {
    marginTop: 20,
    marginBottom: 20,
  },
  imageSkeleton: {
    // height: 200,
    // height handled by aspect ratio wrapper in LazyImage
  },
  dialogPaper: {
    minHeight: "90vh",
    maxHeight: "90vh",
  },
  dialogContent: {
    padding: 20,
  },
  appBar: {
    position: "relative",
  },
  dialogTitle: {
    flex: 1,
  },
  viewAssetsButton: {
    marginTop: 10,
  },
  assetCount: {
    fontSize: 14,
    color: "#666",
    marginTop: 5,
  },
}));


const getAspectRatio = (size, fileName, selectedCategoryId) => {
  const name = fileName?.toLowerCase() || "";

  if (selectedCategoryId === 2) {
    if (size === "Landscape") {
      if (name.startsWith("1_")) return 3796 / 3000; // Front Cover
      if (name.startsWith("24_")) return 4010 / 3000; // Back Cover
      return 3508 / 2400;  // Inner Pages
    }

    if (size === "Square") {
      if (name.startsWith("1_")) return 2737 / 3000; // Front Cover
      if (name.startsWith("24_")) return 2961 / 3000; // Back Cover
      return 2400 / 2400;  // Inner Pages
    }
  } else if (selectedCategoryId === 9) {
    if (size === "Regular Mug") return 2550 / 1050;
    if (size === "Big Mug") return 2550 / 1125;
    return 2550 / 1050; // fallback to Regular
  }

  return 1; // fallback 1:1

};


const getExpectedAspectRatio = (size, fileName, selectedCategoryId) => {
  // ic and fl share the same aspect ratio — ic is just a scaled-down version.
  const name = fileName?.toLowerCase() || "";

  if (selectedCategoryId === 2) {
    if (size === "Landscape") {
      if (name.startsWith("1_") || name.startsWith("page1_")) return 3796 / 3000; // 1.2653 Front Cover
      if (name.startsWith("24_") || name.startsWith("page24_")) return 4010 / 3000; // 1.3367 Back Cover
      return 3508 / 2400; // 1.4617 Inner Pages
    }
    if (size === "Square") {
      if (name.startsWith("1_") || name.startsWith("page1_")) return 2737 / 3000; // 0.9123 Front Cover
      if (name.startsWith("24_") || name.startsWith("page24_")) return 2961 / 3000; // 0.9870 Back Cover
      return 2400 / 2400; // 1.0000 Inner Pages
    }
  } else if (selectedCategoryId === 9) {
    if (size === "Regular Mug") return 2550 / 1050; // 2.4286
    if (size === "Big Mug") return 2550 / 1125; // 2.2667
    return 2550 / 1050; // fallback
  }

  return null; // no rule — skip validation
};

const ASPECT_RATIO_TOLERANCE = 0.01; // ±0.01 ratio tolerance (~0.8%)

const LazyImage = ({ src, alt, className, aspectRatio, selectedCategoryId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const classes = useStyles();
  console.log("selectedCategoryId in LazyImage:", selectedCategoryId, typeof selectedCategoryId);

  useEffect(() => {
    if (!src) return;
    let objectUrl = null;

    const fetchImage = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(src, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        console.error("Image fetch error:", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);

  const paddingTop = aspectRatio ? `${(1 / aspectRatio) * 100}%` : "100%";

  return (
    <div style={{ position: "relative", width: "100%", paddingTop }}>
      {isLoading && (
        <Skeleton
          variant="rect"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      )}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#a9a9a96b",
            display: "block",

          }}
        />
      )}
    </div>
  );
};

const getGridSize = (selectedCategoryId) => {
  if (selectedCategoryId === 9) {
    return { xs: 8, sm: 8, md: 8, lg: 8 }; // full width for mug
  }
  return { xs: 6, sm: 4, md: 3, lg: 2 }; // default
};

const AssetsModal = ({ open, onClose, product, getImageUrl, isAssetsLoading, onAssetUpload, uploadingAssetId, classes, selectedCategoryId, onDescriptionSave, isSavingDescription, onValidationError, onProductFieldChange }) => {
  const [activeTab, setActiveTab] = useState(0);

  // ── Status / Trending local state ──
  const [isActive, setIsActive] = useState(1);
  const [isTrending, setIsTrending] = useState(0);
  // ── Edit states ──
  const [editingThemeLabel, setEditingThemeLabel] = useState(false);
  const [editingProductLabel, setEditingProductLabel] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  const [themeLabelValue, setThemeLabelValue] = useState("");
  const [productLabelValue, setProductLabelValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");

  // ── Preview/Thumbnail states ──
  const [previewImage, setPreviewImage] = useState(null);
  const [thumbnailImage, setThumbnailImage] = useState(null);

  useEffect(() => {
    setActiveTab(0);
    setEditingThemeLabel(false);
    setEditingProductLabel(false);
    setEditingDescription(false);
    setThemeLabelValue(product?.userLabel || "");
    setProductLabelValue(product?.label || "");
    setDescriptionValue(product?.description || "");
    setIsActive(product?.isActive != null ? product.isActive : "1");     // ← ADD
    setIsTrending(product?.isTrending != null ? product.isTrending : "0"); // ← ADD
    setPreviewImage(null);
    setThumbnailImage(null);
  }, [open, product]);

  if (!product) return null;

  const assetGroups = product.assetGroups || [];
  const currentGroup = assetGroups[activeTab];

  const handleImageUpload = (file, setter) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setter({ file, preview, dimensions: { width: img.width, height: img.height } });
    };
    img.src = preview;
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <AppBar className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" className={classes.dialogTitle}>
            {product.label} - Assets
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent className={classes.dialogContent}>
        <Box mb={2}>
          <Typography variant="body1" color="textSecondary">
            Size: {product.size} | Product ID: {product.productId}
          </Typography>
        </Box>


        {/* ── Active / Trending Radio Buttons ── */}
        <Box mb={2} display="flex" alignItems="center" style={{ gap: 32 }}>

          {/* ── Active / Inactive ── */}
          <Box>
            <Typography variant="caption" style={{ fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
              Status
            </Typography>
            <RadioGroup
              row
              value={String(isActive)}
              onChange={async (e) => {
                const newValue = parseInt(e.target.value);
                const formData = new FormData();
                formData.append("productId", product.productId);
                formData.append("actionTypeId", newValue);
                const res = await HttpRequest(
                  "POST",
                  `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsStatusUpdate`,
                  formData,
                  "authorize"
                );
                if (res.isSuccess) {
                  setIsActive(newValue);
                  onProductFieldChange({ isActive: newValue });
                } else {
                  onValidationError("Failed to update status.");
                }
              }}
            >
              <FormControlLabel
                value="1"
                control={<Radio size="small" style={{ color: "#4caf50" }} />}
                label={<Typography variant="body2">Active</Typography>}
              />
              <FormControlLabel
                value="0"
                control={<Radio size="small" style={{ color: "#f44336" }} />}
                label={<Typography variant="body2">Inactive</Typography>}
              />
            </RadioGroup>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* ── Trending / Not Trending ── */}
          <Box>
            <Typography variant="caption" style={{ fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
              Trending
            </Typography>
            <RadioGroup
              row
              value={String(isTrending)}
              onChange={async (e) => {
                const newValue = parseInt(e.target.value);
                const formData = new FormData();
                formData.append("productId", product.productId);
                formData.append("actionTypeId", newValue);
                const res = await HttpRequest(
                  "POST",
                  `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsTrendingUpdate`,
                  formData,
                  "authorize"
                );
                if (res.isSuccess) {
                  setIsTrending(newValue);
                  onProductFieldChange({ isTrending: newValue });
                } else {
                  onValidationError("Failed to update trending status.");
                }
              }}
            >
              <FormControlLabel
                value="1"
                control={<Radio size="small" style={{ color: "#ff9800" }} />}
                label={<Typography variant="body2">Trending</Typography>}
              />
              <FormControlLabel
                value="0"
                control={<Radio size="small" style={{ color: "#9e9e9e" }} />}
                label={<Typography variant="body2">Not Trending</Typography>}
              />
            </RadioGroup>
          </Box>

        </Box>



        {/* ── Description ── */}
        <Box mb={2}>
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
            multiline
            rows={3}
            style={{ width: "100%" }}
            inputProps={{ maxLength: 236 }}
            helperText={editingDescription ? `${descriptionValue.length} / 236` : ""}
            InputProps={{
              readOnly: !editingDescription,
              endAdornment: (
                <InputAdornment position="end" style={{ alignSelf: "flex-start", marginTop: 8 }}>
                  {editingDescription ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          onClick={() => onDescriptionSave(descriptionValue, () => setEditingDescription(false))}
                          style={{ color: "#4caf50" }}
                          disabled={isSavingDescription}
                        >
                          {isSavingDescription ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDescriptionValue(product?.description || "");
                            setEditingDescription(false);
                          }}
                          style={{ color: "#f44336" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title="Edit Description">
                      <IconButton size="small" onClick={() => setEditingDescription(true)} style={{ color: "#1976d2" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ── Preview & Thumbnail Images ── */}
        <Box mb={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#555", marginBottom: 8 }}>
            {selectedCategoryId == 6 ? "Preview & Thumbnail Images" : "Preview Image"}
          </Typography>
          <Grid container spacing={2}>

            {/* ── Preview Image ── */}
            <Grid item xs={12} sm={selectedCategoryId == 6 ? 6 : 4}>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                Preview Image
              </Typography>
              <input
                type="file"
                accept="image/*"
                id="modal-preview-upload"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload(file, setPreviewImage);
                  e.target.value = "";
                }}
              />
              <label htmlFor="modal-preview-upload" style={{ cursor: "pointer", display: "block" }}>
                <Tooltip title={previewImage ? "Click to replace" : "Click to upload"}>
                  <div
                    style={{
                      width: "100%",
                      height: 220,  // ← was 160
                      backgroundColor: "#f5f5f5",
                      border: previewImage ? "1px solid #1976d2" : "2px dashed #ccc",
                      borderRadius: 8,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                  >
                    {previewImage ? (
                      <img
                        src={previewImage.preview}
                        alt="preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : product?.previewFile ? (
                      <img
                        src={getImageUrl(product.path + "/" + product.previewFile)}
                        alt="preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <div style={{ textAlign: "center", color: "#bbb" }}>
                        <span style={{ fontSize: 32 }}>🖼️</span>
                        <Typography variant="caption" style={{ color: "#bbb", display: "block", marginTop: 4 }}>
                          Click to upload
                        </Typography>
                      </div>
                    )}
                  </div>
                </Tooltip>
              </label>

              {/* ── File info ── */}
              <Box mt={0.5} style={{ minHeight: 40 }}>
                {previewImage ? (
                  <>
                    <Typography variant="caption" color="textSecondary"
                      style={{ display: "block", wordBreak: "break-all" }}
                      title={previewImage.file.name}
                    >
                      📄 {previewImage.file.name}
                    </Typography>
                    {previewImage.dimensions && (
                      <Typography variant="caption" style={{ color: "#888", display: "block" }}>
                        📐 {previewImage.dimensions.width} × {previewImage.dimensions.height} px
                      </Typography>
                    )}
                    <Box display="flex" justifyContent="flex-end">
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => setPreviewImage(null)} style={{ color: "#f44336", padding: 4 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                ) : product?.previewFile ? (
                  <Typography variant="caption" color="textSecondary"
                    style={{ display: "block", wordBreak: "break-all" }}
                  >
                    📄 {product.previewFile}
                  </Typography>
                ) : null}
              </Box>
            </Grid>

            {/* ── Thumbnail Image — Calendar only ── */}
            {selectedCategoryId == 6 && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                  Thumbnail Image
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  id="modal-thumbnail-upload"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(file, setThumbnailImage);
                    e.target.value = "";
                  }}
                />
                <label htmlFor="modal-thumbnail-upload" style={{ cursor: "pointer", display: "block" }}>
                  <Tooltip title={thumbnailImage ? "Click to replace" : "Click to upload"}>
                    <div
                      style={{
                        width: "100%",
                        height: 220,  // ← was 160
                        backgroundColor: "#f5f5f5",
                        border: thumbnailImage ? "1px solid #1976d2" : "2px dashed #ccc",
                        borderRadius: 8,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "opacity 0.2s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                    >
                      {thumbnailImage ? (
                        <img
                          src={thumbnailImage.preview}
                          alt="thumbnail"
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : product?.thumbnailFile ? (
                        <img
                          src={getImageUrl(product.path + "/" + product.thumbnailFile)}
                          alt="thumbnail"
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#bbb" }}>
                          <span style={{ fontSize: 32 }}>🖼️</span>
                          <Typography variant="caption" style={{ color: "#bbb", display: "block", marginTop: 4 }}>
                            Click to upload
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </label>

                {/* ── File info ── */}
                <Box mt={0.5} style={{ minHeight: 40 }}>
                  {thumbnailImage ? (
                    <>
                      <Typography variant="caption" color="textSecondary"
                        style={{ display: "block", wordBreak: "break-all" }}
                        title={thumbnailImage.file.name}
                      >
                        📄 {thumbnailImage.file.name}
                      </Typography>
                      {thumbnailImage.dimensions && (
                        <Typography variant="caption" style={{ color: "#888", display: "block" }}>
                          📐 {thumbnailImage.dimensions.width} × {thumbnailImage.dimensions.height} px
                        </Typography>
                      )}
                      <Box display="flex" justifyContent="flex-end">
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => setThumbnailImage(null)} style={{ color: "#f44336", padding: 4 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  ) : product?.thumbnailFile ? (
                    <Typography variant="caption" color="textSecondary"
                      style={{ display: "block", wordBreak: "break-all" }}
                    >
                      📄 {product.thumbnailFile}
                    </Typography>
                  ) : null}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
        <Divider style={{ marginBottom: 12 }} />

        {isAssetsLoading ? (
          <Box textAlign="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : assetGroups.length > 0 ? (
          <>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newVal) => setActiveTab(newVal)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 16 }}
            >
              {assetGroups.map((group, index) => (
                <Tab
                  key={group.assetTypeId}
                  label={`${group.assetTypeLabel} (${group.assets.length})`}
                />
              ))}
            </Tabs>

            {/* Assets Grid for active tab */}
            {currentGroup && currentGroup.assets.length > 0 ? (
              <Grid container spacing={3} justifyContent={selectedCategoryId == 9 ? "center" : "flex-start"}>
                {currentGroup.assets.map((asset) => {
                  const gridSize = getGridSize(selectedCategoryId);
                  return (
                    <Grid item xs={gridSize.xs} sm={gridSize.sm} md={gridSize.md} lg={gridSize.lg} key={asset.id}>
                      <Card className={classes.assetCard} elevation={2}>
                        <LazyImage
                          src={getImageUrl(asset.fileUrl)}
                          alt={asset.fileName}
                          className={classes.assetImage}
                          aspectRatio={getAspectRatio(product.size, asset.fileName, selectedCategoryId)}
                          selectedCategoryId={selectedCategoryId}
                        />
                        <CardContent>
                          <Typography className={classes.assetFileName} title={asset.fileName}>
                            {asset.fileName}
                          </Typography>

                          {/* Layout ID + Upload Button Row */}
                          <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                            <Typography variant="caption" color="textSecondary">
                              Layout ID: {asset.themeLayoutId}
                            </Typography>

                            <input
                              type="file"
                              accept="image/*"
                              id={`upload-${asset.id}`}
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) { e.target.value = ""; return; }
                                const objectUrl = URL.createObjectURL(file);
                                const img = new Image();
                                img.onload = () => {
                                  URL.revokeObjectURL(objectUrl);
                                  const expectedRatio = getExpectedAspectRatio(product.size, asset.fileName, selectedCategoryId);
                                  if (expectedRatio) {
                                    const actualRatio = img.width / img.height;
                                    const diff = Math.abs(actualRatio - expectedRatio);
                                    if (diff > ASPECT_RATIO_TOLERANCE) {
                                      onValidationError(
                                        `Wrong aspect ratio for ${asset.fileName}. ` +
                                        `Expected ~${expectedRatio.toFixed(4)} (±${ASPECT_RATIO_TOLERANCE}), ` +
                                        `got ${actualRatio.toFixed(4)} (${img.width}×${img.height}px).`
                                      );
                                      return;
                                    }
                                  }
                                  onAssetUpload(asset, file);
                                };
                                img.src = objectUrl;
                                e.target.value = "";
                              }}
                            />

                            {uploadingAssetId === asset.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <label htmlFor={`upload-${asset.id}`} style={{ cursor: "pointer" }}>
                                <Tooltip title="Upload new image">
                                  <IconButton size="small" component="span">
                                    <PublishIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </label>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No assets in this category.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No assets found.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CATEGORIES = [
  { categoryId: 2, categoryLabel: "Themed Photobooks" },
  { categoryId: 6, categoryLabel: "Photo Calendars" },
  { categoryId: 9, categoryLabel: "Photo Mugs" },
];

const handleAddNewTheme = () => {
  window.location = "#/F15ThemeProductsAssetsForm";
};

const handleAddNewThemeWithFolder = () => {
  window.location = "#/F15ThemeProductsAssetsFormWithFolder";
};


const F15AllThemedAssetsView = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]); // just for dropdown [{categoryId, categoryLabel, productCount}]
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedCategoryProducts, setSelectedCategoryProducts] = useState(null); // {categoryLabel, products[]}
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [isLoginMenu, setIsLoginMenu] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);
  const [uploadingAssetId, setUploadingAssetId] = useState(null);
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // On mount: fetch all categories for dropdown only
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch all categories (light — just for dropdown list)
  const fetchCategories = async () => {
    setIsLoading(true);
    const res = await HttpRequest(
      "GET",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesView`,
      null,
      "authorizeJson"
    );

    if (res.isSuccess) {
      const data = res.data || [];
      // Build dropdown list from response
      const list = data.map((category) => ({
        categoryId: category.categoryId,
        categoryLabel: category.categoryLabel,
        productCount: category.products ? category.products.length : 0,
      }));
      setCategoriesList(list);
    } else {
      if (res.statusCode === 401) {
        setIsLoginMenu(true);
      } else {
        handleSnackbar(true, res.message || "Failed to load categories", "error");
      }
    }
    setIsLoading(false);
  };

  const handleAssetUpload = async (asset, file) => {
    setUploadingAssetId(asset.id);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("assetId", asset.id);
    formData.append("themeLayoutId", asset.themeLayoutId);
    formData.append("productId", selectedProduct.productId);

    const res = await HttpRequest(
      "POST",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsUpdate`,
      formData,
      "authorize"
    );

    if (res.isSuccess) {
      handleSnackbar(true, "Asset updated successfully", "success");
      // Reload assets for current product
      const reloadRes = await HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${selectedProduct.productId}`,
        null,
        "authorizeJson"
      );
      if (reloadRes.isSuccess) {
        const data = reloadRes.data && reloadRes.data.length > 0 ? reloadRes.data[0] : null;
        if (data) {
          setSelectedProduct((prev) => ({
            ...prev,
            label: data.label || prev.label,
            userLabel: data.userLabel || prev.userLabel,
            description: data.description || prev.description,
            previewFile: data.previewFile || prev.previewFile,
            thumbnailFile: data.thumbnailFile || prev.thumbnailFile,
            path: data.path || prev.path,
            assetGroups: data.assetsTypes || [],
          }));
        }
      }
    } else {
      handleSnackbar(true, res.message || "Upload failed", "error");
    }

    setUploadingAssetId(null);
  };
  const handleProductFieldChange = (fields) => {
    setSelectedProduct((prev) => ({ ...prev, ...fields }));

    // ── Update selectedCategoryProducts (for main page buttons) ──
    setSelectedCategoryProducts((prev) =>
      prev.map((themeItem) => ({
        ...themeItem,
        themeProducts: themeItem.themeProducts
          ? themeItem.themeProducts.map((p) =>
            p.productId === selectedProduct.productId
              ? { ...p, ...fields }
              : p
          )
          : [],
      }))
    );
  };

  const handleDescriptionSave = async (description, onSuccess) => {
    setIsSavingDescription(true);

    const formData = new FormData();
    formData.append("productId", selectedProduct.productId);
    formData.append("description", description);

    const res = await HttpRequest(
      "POST",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsDescriptionUpdate`,
      formData,
      "authorize"
    );

    if (res.isSuccess) {
      handleSnackbar(true, "Description updated successfully", "success");
      // Reload the same product page — identical to handleAssetUpload reload
      const reloadRes = await HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${selectedProduct.productId}`,
        null,
        "authorizeJson"
      );
      if (reloadRes.isSuccess) {
        const data = reloadRes.data && reloadRes.data.length > 0 ? reloadRes.data[0] : null;
        if (data) {
          setSelectedProduct((prev) => ({
            ...prev,
            label: data.label || prev.label,
            userLabel: data.userLabel || prev.userLabel,
            description: data.description || prev.description,
            previewFile: data.previewFile || prev.previewFile,
            thumbnailFile: data.thumbnailFile || prev.thumbnailFile,
            path: data.path || prev.path,
            assetGroups: data.assetsTypes || [],
          }));
        }
      }
      if (onSuccess) onSuccess();
    } else {
      handleSnackbar(true, res.message || "Failed to update description", "error");
    }

    setIsSavingDescription(false);
  };

  // On dropdown change: call API for selected category
  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    setSelectedCategoryProducts(null);

    if (!categoryId) return;

    setIsProductsLoading(true);
    const res = await HttpRequest(
      "GET",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesView?categoryId=${categoryId}`,
      null,
      "authorizeJson"
    );

    if (res.isSuccess) {
      const data = res.data || [];
      // Assuming API returns array — take first match or adjust based on your API response shape
      // const categoryData = Array.isArray(data) ? data[0] : data;
      setSelectedCategoryProducts(Array.isArray(data) ? data : [data]);

    } else {
      if (res.statusCode === 401) {
        setIsLoginMenu(true);
      } else {
        handleSnackbar(true, res.message || "Failed to load products", "error");
      }
    }
    setIsProductsLoading(false);
  };

  const handleSnackbar = (isOpen, message, severity) => {
    setIsOpenSnackbar(isOpen);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleOpenModal = async (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    setIsAssetsLoading(true);

    const res = await HttpRequest(
      "GET",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${product.productId}`,
      null,
      "authorizeJson"
    );

    if (res.isSuccess) {
      const data = res.data && res.data.length > 0 ? res.data[0] : null;
      if (data) {
        setSelectedProduct({
          ...product,
          // ── new fields from updated API ──
          label: data.label || product.label,
          userLabel: data.userLabel || product.userLabel,
          description: data.description || "",
          previewFile: data.previewFile || "",
          thumbnailFile: data.thumbnailFile || "",
          path: data.path || "",
          isActive: data.isActive != null ? data.isActive : "1",
          isTrending: data.isTrending != null ? data.isTrending : "0",
          // ── map assetsTypes → assetGroups ──
          assetGroups: data.assetsTypes || [],
        });
      }
    } else {
      if (res.statusCode === 401) {
        setIsLoginMenu(true);
      } else {
        handleSnackbar(true, res.message || "Failed to load assets", "error");
      }
    }
    setIsAssetsLoading(false);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const getImageUrl = (fileUrl) => {
    const url = `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/albums/C01ImageView?fileName=${fileUrl}`;
    console.log("Image URL:", url);
    return url;
  };

  if (isLoading) {
    return (
      <Fragment>
        <NavBar isAuthorize />
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <LoginMenu
        reload={true}
        open={isLoginMenu}
        handleClose={() => setIsLoginMenu(false)}
      />
      <CustomizedSnackbar
        isOpen={isOpenSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleCloseSnackbar={() => handleSnackbar(false, "", "")}
        autoHideDuration={3000}
      />
      <NavBar isAuthorize />
      <AssetsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        product={selectedProduct}

        getImageUrl={getImageUrl}
        isAssetsLoading={isAssetsLoading}
        onAssetUpload={handleAssetUpload}
        uploadingAssetId={uploadingAssetId}
        classes={classes}
        selectedCategoryId={selectedCategoryId}
        onDescriptionSave={handleDescriptionSave}
        isSavingDescription={isSavingDescription}
        onProductFieldChange={handleProductFieldChange}
        onValidationError={(msg) => handleSnackbar(true, msg, "error")}
      />

      <div className={classes.root}>

        {/* ── DROPDOWN ── */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={3}
          p={2}
          style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            Select Category:
          </Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 250 }}>
            <Select
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>-- Select Category --</em>
              </MenuItem>
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.categoryId} value={cat.categoryId}>
                  {cat.categoryLabel} &nbsp;
                  <span style={{ color: "#aaa", fontSize: "11px" }}>
                    (ID: {cat.categoryId})
                  </span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box style={{ marginLeft: "auto", display: "flex", gap: "16px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddNewThemeWithFolder()}
              style={{ marginLeft: "auto", backgroundColor: "#1976d2", color: "white" }}
            >
              + Add New Theme Via Folder
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddNewTheme()}
              style={{ marginLeft: "auto", backgroundColor: "#1976d2", color: "white" }}
            >
              + Add New Theme Manually
            </Button>
          </Box>



        </Box>

        {/* ── PRODUCTS LOADING ── */}
        {isProductsLoading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress size={36} />
          </Box>
        )}

        {/* ── PRODUCTS GRID ── */}
        {!isProductsLoading && selectedCategoryProducts && selectedCategoryProducts.length > 0 && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            {selectedCategoryProducts.map((themeItem) => (
              <Box key={themeItem.themeId}>
                {themeItem.themeProducts ? (
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      backgroundColor: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      height: "100%",
                    }}
                  >
                    {/* Theme Header */}
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>
                      {themeItem.themeId} : {themeItem.themeLabel}
                    </span>
                    <span style={{ fontSize: "11px", color: "#888" }}>
                      {themeItem.themeProducts.length} products
                    </span>

                    {/* Product Buttons */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                      {themeItem.themeProducts.map((product) => {
                        console.log("ThemeId:", themeItem.themeId, "| ProductId:", product.productId, "| isTrending:", product.isTrending, typeof product.isTrending);
                        return (
                          <button
                            key={product.productId}
                            style={{
                              padding: "3px 10px",
                              fontSize: "11px",
                              border: `1px solid ${product.isActive == 0 ? "#f44336" : "#4caf50"}`,
                              borderRadius: "4px",
                              color: product.isActive == 0 ? product.isTrending == 0 ? "#f44336" : "white" : product.isTrending == 0 ? "#4caf50" : "white",
                              backgroundColor: product.isTrending == 1 ? "orange" : "transparent",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenModal(product);
                            }}
                          >
                            {product.label} Assets
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No products available for this theme.
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}


        {/* ── EMPTY STATE ── */}
        {!isProductsLoading && !selectedCategoryProducts && !isLoading && (
          <Box textAlign="center" mt={8}>
            <Typography variant="h6" color="textSecondary">
              👆 Select a category above to view products.
            </Typography>
          </Box>
        )}
      </div>
    </Fragment>
  );
};



export default F15AllThemedAssetsView;
